/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks"
import { classNames } from "utils/preact/class-names"

import {
  CardElement,
  CardElementProps,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { JSX } from "preact/jsx-runtime"
import {
  loadStripe,
  PaymentRequest,
  StripeCardElementOptions,
  StripePaymentRequestButtonElementOptions,
} from "@stripe/stripe-js"
import css from "../style.module.scss"
import { formatPrice } from "../components/formatPrice"
import { $support, usePrices, useSupport } from "../hooks/support"
import { $user, useUser } from "../hooks/user"
import { clear, navigate } from "../hooks/navigation"
import { t } from "../hooks/translations"
import { trackAddPaymentInfo, trackBeginCheckout, trackPurchase } from "../services/analytics"
import { OffersScreen } from "./OffersScreen"

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

export const stripePromise = new Promise((resolve) => {
  const unsubscribe = $support.subscribe((support) => {
    if (support == null) return

    const { stripe_publishable_key: stripePublishableKey } = support
    unsubscribe()
    resolve(loadStripe(stripePublishableKey))
  })
})

export const PaymentScreen = function PaymentScreen() {
  const { offer, email } = useUser()
  const { currency, monthly, annually } = usePrices()
  const support = useSupport()
  const accountEmail = email ?? support.user.email ?? ""
  const activePrice = offer === "monthly" ? monthly : annually
  const beginCheckoutTracked = useRef(false)

  useEffect(() => {
    if (beginCheckoutTracked.current) return

    trackBeginCheckout(offer, activePrice)
    beginCheckoutTracked.current = true
  }, [offer, activePrice])

  return (
    <div className={css["flex-container"]}>
      <div className={css["offers-screen"]}>
        <OffersScreen showContinueButton={false} />
      </div>

      <div className={css.splitter} />

      <div className={css["payment-screen"]}>
        <Elements stripe={stripePromise}>
          <h2 className={css["payment-title"]}>{t("payment_info")}</h2>
          {offer === "yearly" && (
            <>
              <p className={css["payment-account"]}>
                {t("account")} {accountEmail}
              </p>
              <div className={css["payment-info"]}>
                <p className={css["payment-membership"]}>{t("yearly_membership")}</p>
                <p>
                  {formatPrice({
                    prefix: currency.prefix,
                    unitAmount: annually.unit_amount / 100,
                  })}
                </p>
              </div>
              <p className={css["payment-billing"]}>
                {t("sum_billed_yearly", {
                  sum1: formatPrice({
                    prefix: currency.prefix,
                    unitAmount: annually.unit_amount / 100,
                  }),
                  sum2: formatPrice({
                    prefix: currency.prefix,
                    unitAmount: annually.unit_amount / 100 / 12,
                  }),
                })}
              </p>
            </>
          )}
          {offer === "monthly" && (
            <>
              <p className={css["payment-account"]}>
                {t("account")} {accountEmail}
              </p>
              <div className={css["payment-info"]}>
                <p className={css["payment-membership"]}>{t("monthly_membership")}</p>
                <p>
                  {formatPrice({
                    prefix: currency.prefix,
                    unitAmount: monthly.unit_amount / 100,
                  })}
                </p>
              </div>
              <p className={css["payment-billing"]}>
                {t("billed_per_month", {
                  sum: formatPrice({
                    prefix: currency.prefix,
                    unitAmount: monthly.unit_amount / 100,
                  }),
                })}
              </p>
            </>
          )}
          <StripeForm />
        </Elements>
      </div>
    </div>
  )
}

const StripeForm = function StripeForm() {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)

  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState<string>(null)
  const [processing, setProcessing] = useState(false)
  const [initializingPayment, setInitializingPayment] = useState(false)
  const [disabled, setDisabled] = useState(true)

  const { offer } = useUser()
  const { iso, currency, monthly, annually } = usePrices()

  const activePrice = useMemo(
    () => (offer === "monthly" ? monthly : annually),
    [offer, monthly, annually],
  )

  const stripe = useStripe()
  const elements = useElements()

  const requestPayment = useCallback(async () => {
    setInitializingPayment(true)
    try {
      const response = await fetch("/sign_up/stripe_payment", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ stripe_payment: { price_id: activePrice.price_id } }),
      })

      if (!response.ok) {
        const json = await response.json().catch(() => null)
        throw new Error(
          json?.error || json?.message || `${response.status}: ${response.statusText}`,
        )
      }

      const json = await response.json()
      if (typeof json?.clientSecret !== "string" || json.clientSecret.length === 0) {
        throw new Error("Could not initialize payment")
      }

      return {
        clientSecret: json.clientSecret as string,
        subscriptionId: typeof json.subscriptionId === "string" ? json.subscriptionId : null,
      }
    } finally {
      setInitializingPayment(false)
    }
  }, [activePrice.price_id])

  const handleLoadErrors: CardElementProps["onLoadError"] = (event) => {
    setError(String(event.error))
  }

  const handleChange: CardElementProps["onChange"] = (event) => {
    setDisabled(event.empty)
    setError(event.error ? event.error.message : null)
  }

  const handleSubmit: JSX.SubmitEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    if (processing) return

    try {
      setError(null)
      setProcessing(true)

      const { clientSecret, subscriptionId } = await requestPayment()
      trackAddPaymentInfo(offer, activePrice, "card")
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement("card"),
        },
      })

      if (payload.error != null) {
        setError(payload.error.message)
        return
      }

      if (payload.paymentIntent.status !== "succeeded") {
        setError(`Something went wrong.\n(paymentIntent.status is ${payload.paymentIntent.status})`)
        return
      }

      trackPurchase(subscriptionId, offer, activePrice)
      setSucceeded(true)
    } catch (err) {
      setError(String(err))
    } finally {
      setProcessing(false)
    }
  }

  useEffect(() => {
    if (stripe == null) return

    let pr: PaymentRequest
    try {
      pr = stripe.paymentRequest({
        country: iso,
        currency: currency.acronym.toLowerCase(),
        total: {
          label: offer === "monthly" ? "Monthly subscription" : "Yearly subscription",
          amount: activePrice.unit_amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      return
    }

    pr.canMakePayment().then((available) => {
      if (!available) return

      setPaymentRequest(pr)

      pr.on("paymentmethod", async (ev) => {
        try {
          setProcessing(true)
          setError(null)

          const { clientSecret, subscriptionId } = await requestPayment()
          trackAddPaymentInfo(offer, activePrice, "payment_request")
          const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false },
          )

          if (confirmError != null) {
            // eslint-disable-next-line no-console
            console.error(confirmError)

            setError(String(confirmError))
            setProcessing(false)
            ev.complete("fail")
            return
          }

          ev.complete("success")
          let finalPaymentIntent = paymentIntent
          if (finalPaymentIntent.status === "requires_action") {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
              clientSecret,
            )

            if (confirmError != null) {
              // eslint-disable-next-line no-console
              console.error(confirmError)

              setError(String(confirmError))
              setProcessing(false)
              return
            }

            finalPaymentIntent = paymentIntent
          }

          if (finalPaymentIntent.status !== "succeeded") {
            setError(
              `Something went wrong.\n(paymentIntent.status is ${finalPaymentIntent.status})`,
            )
            setProcessing(false)
            return
          }

          setProcessing(false)
          trackPurchase(subscriptionId, offer, activePrice)
          setSucceeded(true)
        } catch (err) {
          setProcessing(false)
          setError(String(err))
          ev.complete("fail")
        }
      })
    })
  }, [stripe, iso, activePrice, currency.acronym, offer, requestPayment])

  useEffect(() => {
    if (!succeeded) return

    $user.setKey("paymentDone", true)

    navigate("Success")
    clear()
  }, [succeeded])

  const prOptions: StripePaymentRequestButtonElementOptions = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        height: "60px",
        buttonSpacing: "16px",
      },
    },
  }

  const cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        lineHeight: "1.5",
      },
    },
  }
  const paymentBusy = processing || initializingPayment

  return (
    <form method="post" onSubmit={handleSubmit}>
      {paymentRequest != null && (
        <>
          <div className={css["payment-request"]}>
            <PaymentRequestButtonElement options={prOptions} />
          </div>

          <p className={css.or}>{t("or")}</p>
        </>
      )}

      <div className={css.card}>
        <CardElement options={cardOptions} onChange={handleChange} onLoadError={handleLoadErrors} />
      </div>

      {paymentBusy && (
        <div style={{ marginBottom: 16 }} className={css.paragraph}>
          {initializingPayment ? "Initializing secure payment..." : "Processing payment..."}
        </div>
      )}
      {error != null && (
        <div style={{ marginBottom: 16 }} className={css["submit-error"]}>
          {error}
        </div>
      )}
      <button
        type="submit"
        className={classNames([css.button, paymentBusy && css["is-loading"]])}
        disabled={paymentBusy || disabled || succeeded}
      >
        {t("continue")}
      </button>
    </form>
  )
}
